import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest, offboardingScopes, onboardingScopes } from '../config/authConfig';
import { getSessionId } from './convexService';

export class AuthService {
  constructor(msalInstance = null) {
    this.msalInstance = msalInstance;
    this.account = null;
  }

  // Set MSAL instance (called from App.js after initialization)
  setMsalInstance(msalInstance) {
    this.msalInstance = msalInstance;
    // Update account if there's one signed in
    const accounts = this.msalInstance?.getAllAccounts() || [];
    if (accounts.length > 0) {
      this.account = accounts[0];
    }
  }

  // Get current account
  getCurrentAccount() {
    if (!this.msalInstance) {
      console.warn('⚠️ MSAL instance not initialized in authService');
      return null;
    }
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      console.warn('⚠️ No MSAL accounts found in authService.getCurrentAccount()');
    }
    return accounts.length > 0 ? accounts[0] : null;
  }

  // Check if user is authenticated
  isAuthenticated() {
    return this.getCurrentAccount() !== null;
  }

  // Login using redirect
  loginRedirect(scopes = loginRequest.scopes) {
    if (!this.msalInstance) {
      throw new Error('MSAL instance not initialized');
    }
    const request = {
      ...loginRequest,
      scopes,
    };
    this.msalInstance.loginRedirect(request);
  }

  // Login using popup
  async loginPopup(scopes = loginRequest.scopes) {
    if (!this.msalInstance) {
      throw new Error('MSAL instance not initialized');
    }
    try {
      const request = {
        ...loginRequest,
        scopes,
      };
      const response = await this.msalInstance.loginPopup(request);
      this.account = response.account;
      return response;
    } catch (error) {
      console.error('Login popup error:', error);
      throw error;
    }
  }

  // Logout
  logout() {
    if (!this.msalInstance) {
      return;
    }
    const logoutRequest = {
      account: this.getCurrentAccount(),
    };
    this.msalInstance.logoutRedirect(logoutRequest);
  }

  // Get access token for Graph API
  async getAccessToken(scopes = loginRequest.scopes) {
    // Check if we're using app-only authentication (client credentials flow)
    const authMode = localStorage.getItem('authMode');

    if (authMode === 'app-only') {
      return this.getAppOnlyToken();
    }

    // Standard OAuth2 flow with MSAL
    try {
      const account = this.getCurrentAccount();
      if (!account) {
        throw new Error('No account found');
      }

      const request = {
        scopes,
        account: account,
      };

      // Try to get token from cache first
      const response = await this.msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      // If silent acquisition fails, try interactive method
      if (error instanceof InteractionRequiredAuthError) {
        // acquireTokenRedirect does not return a value - it redirects the page
        // The token will be available after redirect via handleRedirectPromise()
        const request = {
          scopes,
          account: this.getCurrentAccount(),
        };

        console.log('Interactive authentication required - redirecting...');
        // This will redirect the page and not return
        await this.msalInstance.acquireTokenRedirect(request);

        // Code after redirect will never execute
        // Token will be acquired on page load via handleRedirectPromise()
        throw new Error('Redirecting for authentication');
      } else {
        console.error('Token acquisition error:', error);
        throw error;
      }
    }
  }

  // Get access token using app-only (client credentials) flow
  async getAppOnlyToken() {
    try {
      // Get sessionId from localStorage
      const sessionId = getSessionId();
      if (!sessionId) {
        throw new Error('No session ID found. Please configure your Azure AD credentials in Settings.');
      }

      console.log('🔑 Getting app-only token via Convex backend...');

      // Check if we have a cached token that's still valid
      const cachedToken = this._getAppOnlyCachedToken();
      if (cachedToken) {
        console.log('✅ Using cached app-only token');
        return cachedToken;
      }

      // Import Convex client and API
      const { ConvexHttpClient } = await import('convex/browser');
      const { api } = await import('../convex/_generated/api');
      const client = new ConvexHttpClient(process.env.REACT_APP_CONVEX_URL);

      // Get token from Convex backend (which calls Azure AD server-side)
      console.log('🔑 Requesting token from Convex backend...');
      const result = await client.action(api.authActions.getAppOnlyToken, { sessionId });

      if (!result || !result.accessToken) {
        throw new Error('Failed to get access token from backend');
      }

      // Cache the token with expiration
      this._cacheAppOnlyToken(result.accessToken, result.expiresIn);

      console.log('✅ App-only access token acquired successfully from Convex backend');
      return result.accessToken;
    } catch (error) {
      console.error('❌ Error getting app-only token:', error.message);
      console.error('Full error:', error);
      throw error;
    }
  }

  // Get cached app-only token if still valid
  _getAppOnlyCachedToken() {
    try {
      const cached = localStorage.getItem('appOnlyToken');
      if (!cached) return null;

      const { token, expiresAt } = JSON.parse(cached);
      
      // Check if token is still valid (with 5-minute buffer)
      if (Date.now() < expiresAt - (5 * 60 * 1000)) {
        console.log('✅ Using cached app-only token');
        return token;
      }

      // Token expired, remove it
      localStorage.removeItem('appOnlyToken');
      return null;
    } catch (error) {
      return null;
    }
  }

  // Cache app-only token with expiration
  _cacheAppOnlyToken(token, expiresIn) {
    const expiresAt = Date.now() + (expiresIn * 1000);
    localStorage.setItem('appOnlyToken', JSON.stringify({ token, expiresAt }));
  }

  // Get token with specific scopes for offboarding
  async getOffboardingToken() {
    return this.getAccessToken(offboardingScopes);
  }

  // Get token with specific scopes for onboarding
  async getOnboardingToken() {
    return this.getAccessToken(onboardingScopes);
  }

  // Get token for user management
  async getUserManagementToken() {
    return this.getAccessToken(['User.Read.All', 'User.ReadWrite.All']);
  }

  // Get token for device management (Intune)
  async getDeviceManagementToken() {
    return this.getAccessToken([
      'DeviceManagementManagedDevices.ReadWrite.All',
      'DeviceManagementManagedDevices.PrivilegedOperations.All',
      'DeviceManagementApps.ReadWrite.All'
    ]);
  }

  // Get token for mail management
  async getMailManagementToken() {
    return this.getAccessToken([
      'MailboxSettings.ReadWrite',
      'Mail.ReadWrite'
    ]);
  }

  // Get token for SharePoint/OneDrive
  async getSharePointToken() {
    return this.getAccessToken(['Sites.ReadWrite.All']);
  }

  // Get token for Teams
  async getTeamsToken() {
    return this.getAccessToken(['Team.ReadWrite.All']);
  }

  // Request additional permissions
  async requestAdditionalPermissions(scopes) {
    try {
      const request = {
        scopes,
        prompt: 'consent',
      };
      await this.msalInstance.acquireTokenRedirect(request);
    } catch (error) {
      console.error('Permission request error:', error);
      throw error;
    }
  }

  // Check if user has specific permissions
  async hasPermissions(scopes) {
    try {
      await this.getAccessToken(scopes);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Get user info from Graph API
  async getUserInfo() {
    try {
      const token = await this.getAccessToken(['User.Read']);
      const response = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch user info');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching user info:', error);
      throw error;
    }
  }
}

// Create a singleton instance
export const authService = new AuthService();

// Export class for potential multiple instances
export default AuthService;
